import { queryOptions } from "@tanstack/react-query";

import { useApi } from "@/api/hooks/useApi";
import { commonAPIDataSelector } from "@/helpers/Network/selectors";
import { useProjectId } from "@/hooks/Network/useProjectId";
import { QUERY_KEYS } from "@/query-keys";
import type { ApiQueryParams } from "@/types/api-types";

export type DateRange = "lastMonth" | "last6Months" | "lastYear";

export const useAnalyticsQueries = () => {
  const api = useApi();
  const projectId = useProjectId();

  return {
    getFah: () =>
      queryOptions({
        queryKey: QUERY_KEYS.ANALYTICS_FEELING_AT_HOME(projectId),
        queryFn: () => api.getAnalyticsFeelingAtHomeV1(),
        select: commonAPIDataSelector,
        staleTime: Infinity,
        gcTime: Infinity,
      }),
    getFahStats: () =>
      queryOptions({
        queryKey: QUERY_KEYS.ANALYTICS_FEELING_AT_HOME_STATS(projectId),
        queryFn: () => api.getAnalyticsFeelingAtHomeV1(),
        select: commonAPIDataSelector,
        staleTime: Infinity,
        gcTime: Infinity,
      }),
    getFahMonthlyStats: (dateRange: DateRange) =>
      queryOptions({
        queryKey: QUERY_KEYS.ANALYTICS_FEELING_AT_HOME_MONTHLY(projectId, dateRange),
        queryFn: () => api.getAnalyticsFeelingAtHomeMonthlyV1({ dateRange }),
        select: commonAPIDataSelector,
        staleTime: Infinity,
        gcTime: Infinity,
      }),
    getFahDetails: (query: ApiQueryParams<"getAnalyticsFeelingAtHomeDetailsV2">) =>
      queryOptions({
        queryKey: QUERY_KEYS.ANALYTICS_FEELING_AT_HOME_DETAILS(projectId, query),
        queryFn: () => api.getAnalyticsFeelingAtHomeDetailsV2(query),
        select: commonAPIDataSelector,
      }),
    getFahSuggestions: () =>
      queryOptions({
        queryKey: QUERY_KEYS.ANALYTICS_FEELING_AT_HOME_SUGGESTIONS(projectId),
        queryFn: () => api.getAnalyticsFeelingAtHomeSuggestionsV1(),
        select: commonAPIDataSelector,
        staleTime: Infinity,
        gcTime: Infinity,
      }),
    getFahHistoryDetails: (params: ApiQueryParams<"getAnalyticsFeelingAtHomeDetailsV2">) =>
      queryOptions({
        queryKey: QUERY_KEYS.FEELING_AT_HOME_HISTORY_DETAILS(projectId, params),
        queryFn: () => api.getAnalyticsFeelingAtHomeDetailsV2(params),
        select: commonAPIDataSelector,
      }),
    getFahAiQuestionSummary: ({
      questionId,
      startDate,
      endDate,
    }: {
      questionId: string;
      startDate?: string;
      endDate?: string;
    }) =>
      queryOptions({
        queryKey: QUERY_KEYS.FEELING_AT_HOME_HISTORY_QUESTION_RESPONSE_SUMMARY(projectId, questionId, {
          startDate,
          endDate,
        }),
        queryFn: () =>
          api.getAnalyticsFeelingAtHomeDetailsQuestionsSummaryV2(questionId, {
            startDate,
            endDate,
          }),
        select: commonAPIDataSelector,
        staleTime: 1000 * 60 * 5,
      }),
    getFahExport: (query: ApiQueryParams<"getAnalyticsFeelingAtHomeExportV2">) => ({
      queryKey: QUERY_KEYS.FEELING_AT_HOME_EXPORT(projectId, query),
      queryFn: () => api.getAnalyticsFeelingAtHomeExportV2(query),
    }),
    getCommunityFeedStats: () =>
      queryOptions({
        queryKey: QUERY_KEYS.ANALYTICS_COMMUNITY_FEED_STATS(projectId),
        queryFn: () => api.getAnalyticsCommunityFeedV1(),
        select: commonAPIDataSelector,
        staleTime: Infinity,
        gcTime: Infinity,
      }),
    getContribution: (dateRange: DateRange) =>
      queryOptions({
        queryKey: QUERY_KEYS.ANALYTICS_ACTIVE_CONTRIBUTORS(projectId, dateRange),
        queryFn: () => api.getAnalyticsEngagedResidentsMonthlyV2({ dateRange }),
        select: commonAPIDataSelector,
      }),
    getAdoption: () =>
      queryOptions({
        queryKey: QUERY_KEYS.ANALYTICS_ADOPTION(projectId),
        queryFn: () => api.getAnalyticsAdoptionRateV1(),
        select: commonAPIDataSelector,
      }),
    getAdoptionDetails: (dateRange: DateRange) =>
      queryOptions({
        queryKey: QUERY_KEYS.ANALYTICS_DETAILS_ADOPTION(projectId, dateRange),
        queryFn: () => api.getAnalyticsAdoptionRateDetailsDetailsV2("adoption", { dateRange }),
        select: commonAPIDataSelector,
      }),
    getEngagements: () =>
      queryOptions({
        queryKey: QUERY_KEYS.ANALYTICS_ENGAGED_RESIDENTS(projectId),
        queryFn: () => api.getAnalyticsResidentEngagementV1(),
        select: commonAPIDataSelector,
      }),
    getEngagementsDetails: (dateRange: DateRange) =>
      queryOptions({
        queryKey: QUERY_KEYS.ANALYTICS_DETAILS_ENGAGEMENT(projectId, dateRange),
        queryFn: () => api.getAnalyticsResidentEngagementDetailsV2({ dateRange }),
        select: commonAPIDataSelector,
      }),
    getVacantAddresses: (dateRange: DateRange) =>
      queryOptions({
        queryKey: QUERY_KEYS.ANALYTICS_DETAILS_ADOPTION_VACANT(projectId, dateRange),
        queryFn: () => api.getAnalyticsAdoptionRateDetailsDetailsV2("vacant", { dateRange }),
        select: commonAPIDataSelector,
      }),
    getWidgets: () =>
      queryOptions({
        queryKey: QUERY_KEYS.DATA_WIDGETS_LIST(projectId),
        queryFn: () => api.getWidgetsActiveV1(),
        select: commonAPIDataSelector,
      }),
    getSocialContributions: (dateRange: DateRange) =>
      queryOptions({
        queryKey: QUERY_KEYS.ANALYTICS_ACTIVE_CONTRIBUTORS(projectId, dateRange),
        queryFn: () => api.getAnalyticsEngagedResidentsMonthlyV2({ dateRange: dateRange }),
        select: commonAPIDataSelector,
      }),
    getAskForHelpStats: (dateRange: DateRange) =>
      queryOptions({
        queryKey: QUERY_KEYS.ANALYTICS_ASK_FOR_HELP_STATS(projectId, dateRange),
        queryFn: () => api.getAnalyticsAskForHelpMonthlyV1({ dateRange }),
        select: commonAPIDataSelector,
      }),
  };
};
